import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import type { DriveEntry } from '@/lib/drive/driveClient';
import { driveClient } from '@/lib/drive/driveClient';

interface DriveShareDialogProps {
  entry: DriveEntry | null;
  onClose: () => void;
}

/**
 * The share dialog: link options (password, expiry, download limit, view-only) plus
 * sharing to a person by email. Creating the link answers once with its URL; the
 * options map onto the vault's share row one to one.
 */
export function DriveShareDialog({ entry, onClose }: DriveShareDialogProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('7');
  const [maxDownloads, setMaxDownloads] = useState('');
  const [viewOnly, setViewOnly] = useState(false);
  const [sharedWithEmail, setSharedWithEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    if (!entry) return;
    setBusy(true);
    setError(null);
    try {
      const days = Number(expiresInDays);
      const max = Number(maxDownloads);
      const share = await driveClient.createShareWithOptions(entry.path, {
        ...(password ? { password } : {}),
        ...(Number.isFinite(days) && days > 0 ? { expiresInDays: days } : {}),
        ...(Number.isFinite(max) && max > 0 ? { maxDownloads: max } : {}),
        allowDownload: !viewOnly,
        ...(sharedWithEmail.trim() ? { sharedWithEmail: sharedWithEmail.trim() } : {}),
      });
      // Recipients land on the public share page (unlock + download), not a raw API URL.
      setLinkUrl(`${window.location.origin}/s/${share.shortId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!linkUrl) return;
    await navigator.clipboard.writeText(linkUrl).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Transition show={!!entry} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-40 overflow-hidden" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="absolute inset-0 bg-overlay" />
        </Transition.Child>
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md overflow-hidden rounded-xl border border-border-color bg-card shadow-xl">
              <div className="border-b border-border-color px-4 py-3">
                <Dialog.Title className="truncate whitespace-nowrap text-sm font-medium text-foreground">
                  {t('drive.shareTitle', { name: entry?.name ?? '' })}
                </Dialog.Title>
              </div>

              {linkUrl ? (
                <div className="flex flex-col gap-3 p-4">
                  <p className="text-sm text-muted-foreground">{t('drive.shareCreated')}</p>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={linkUrl}
                      onFocus={(event) => event.currentTarget.select()}
                      className="min-w-0 flex-1 rounded-md border border-border-color bg-background px-2 py-1.5 text-xs text-foreground"
                    />
                    <button
                      type="button"
                      onClick={() => void copy()}
                      className="flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-background transition-opacity hover:opacity-90"
                    >
                      {copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
                      {copied ? t('drive.copied') : t('drive.copy')}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setLinkUrl(null);
                      setPassword('');
                      setMaxDownloads('');
                      setSharedWithEmail('');
                      setViewOnly(false);
                    }}
                    className="whitespace-nowrap rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    {t('drive.shareAnother')}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3 p-4">
                  <Field label={t('drive.sharePassword')}>
                    <input
                      type="text"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder={t('drive.sharePasswordHint')}
                      className="w-full rounded-md border border-border-color bg-background px-2 py-1.5 text-sm text-foreground"
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t('drive.shareExpiry')}>
                      <select
                        value={expiresInDays}
                        onChange={(event) => setExpiresInDays(event.target.value)}
                        className="w-full whitespace-nowrap rounded-md border border-border-color bg-background px-2 py-1.5 text-sm text-foreground"
                      >
                        <option value="1">{t('drive.shareExpiry1')}</option>
                        <option value="7">{t('drive.shareExpiry7')}</option>
                        <option value="30">{t('drive.shareExpiry30')}</option>
                        <option value="0">{t('drive.shareExpiryNever')}</option>
                      </select>
                    </Field>
                    <Field label={t('drive.shareMaxDownloads')}>
                      <input
                        type="number"
                        min="0"
                        value={maxDownloads}
                        onChange={(event) => setMaxDownloads(event.target.value)}
                        placeholder="∞"
                        className="w-full rounded-md border border-border-color bg-background px-2 py-1.5 text-sm text-foreground"
                      />
                    </Field>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={viewOnly}
                      onChange={(event) => setViewOnly(event.target.checked)}
                      className="h-4 w-4 rounded border-border-color accent-[var(--accent)]"
                    />
                    {t('drive.shareViewOnly')}
                  </label>
                  <Field label={t('drive.shareWithPerson')}>
                    <input
                      type="email"
                      value={sharedWithEmail}
                      onChange={(event) => setSharedWithEmail(event.target.value)}
                      placeholder="name@example.com"
                      className="w-full rounded-md border border-border-color bg-background px-2 py-1.5 text-sm text-foreground"
                    />
                  </Field>
                  {error && <p className="text-sm text-destructive">{error}</p>}
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={onClose}
                      className="whitespace-nowrap rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void create()}
                      className="flex-shrink-0 whitespace-nowrap rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
                    >
                      {busy ? t('common.working') : t('drive.createLink')}
                    </button>
                  </div>
                </div>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span className="whitespace-nowrap text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
